import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS } from "../constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  "Entretien de bureaux",
  "Nettoyage de vitres",
  "Désinfection",
  "Traitement des sols",
  "Nettoyage après chantier",
  "Nettoyage industriel",
  "Haute pression / parking",
  "Autre prestation",
];

const LOCATION_TYPES = [
  "Bureaux",
  "Commerce",
  "Restaurant",
  "Hôtel",
  "Copropriété",
  "Cabinet médical",
  "Établissement scolaire",
  "Entrepôt / industriel",
  "Autre",
];

const FREQUENCIES = [
  "Ponctuel",
  "Hebdomadaire",
  "2-3 fois / semaine",
  "Quotidien",
  "Mensuel",
  "À définir",
];

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  service_type: "",
  location_type: "",
  surface: "",
  frequency: "",
  city: "",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.full_name ||
      !form.email ||
      !form.phone ||
      !form.service_type ||
      !form.location_type
    ) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSent(true);
      setForm(initialForm);
      toast.success("Demande envoyée ! Nous vous recontactons sous 24h.");
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="relative py-24 md:py-32 bg-white" data-testid="quote-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left info */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit">
            <div className="text-xs uppercase tracking-[0.3em] text-[#2E9E6E] font-medium mb-4">
              — Demande de devis
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-[#0F2B5B] leading-[1.02] tracking-tight">
              Parlez-nous de votre projet.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Remplissez le formulaire ci-contre : nous revenons vers vous sous
              24h ouvrées pour programmer une visite gratuite et vous proposer
              un devis sur mesure.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="flex items-start gap-4 group"
                data-testid="contact-phone-link"
              >
                <div className="mt-1 w-10 h-10 border border-slate-200 flex items-center justify-center group-hover:bg-[#0F2B5B] group-hover:border-[#0F2B5B] transition-colors">
                  <Phone className="w-4 h-4 text-[#0F2B5B] group-hover:text-white" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Téléphone</div>
                  <div className="font-display text-lg font-semibold text-[#0F2B5B] group-hover:text-[#2E9E6E] transition-colors">
                    {BUSINESS.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-start gap-4 group"
                data-testid="contact-email-link"
              >
                <div className="mt-1 w-10 h-10 border border-slate-200 flex items-center justify-center group-hover:bg-[#0F2B5B] group-hover:border-[#0F2B5B] transition-colors">
                  <Mail className="w-4 h-4 text-[#0F2B5B] group-hover:text-white" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Email</div>
                  <div className="font-display text-lg font-semibold text-[#0F2B5B] group-hover:text-[#2E9E6E] transition-colors break-all">
                    {BUSINESS.email}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="mt-1 w-10 h-10 border border-slate-200 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#0F2B5B]" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Zone d'intervention</div>
                  <div className="font-display text-lg font-semibold text-[#0F2B5B]">
                    {BUSINESS.zone}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative bg-[#0F2B5B] text-white p-10 md:p-14 flex flex-col items-start"
                data-testid="quote-success"
              >
                <CheckCircle2 className="w-12 h-12 text-[#2E9E6E] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-3xl font-semibold leading-tight">
                  Demande bien reçue.
                </h3>
                <p className="mt-4 text-slate-300 leading-relaxed max-w-md">
                  Merci pour votre confiance. Un membre de notre équipe vous
                  contactera sous 24h ouvrées pour organiser une visite et
                  préparer votre devis personnalisé.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-8 text-sm font-medium text-[#2E9E6E] hover:text-white transition-colors"
                  data-testid="quote-new-btn"
                >
                  ← Envoyer une autre demande
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-[#F8FAFC] border border-slate-200 p-8 md:p-10 space-y-6"
                data-testid="quote-form"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <FormField label="Nom complet *" id="full_name">
                    <Input
                      id="full_name"
                      value={form.full_name}
                      onChange={(e) => update("full_name", e.target.value)}
                      placeholder="Jean Dupont"
                      className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E]"
                      data-testid="input-full-name"
                      required
                    />
                  </FormField>
                  <FormField label="Entreprise" id="company">
                    <Input
                      id="company"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      placeholder="Nom de votre société"
                      className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E]"
                      data-testid="input-company"
                    />
                  </FormField>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField label="Email *" id="email">
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="jean@entreprise.fr"
                      className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E]"
                      data-testid="input-email"
                      required
                    />
                  </FormField>
                  <FormField label="Téléphone *" id="phone">
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="06 00 00 00 00"
                      className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E]"
                      data-testid="input-phone"
                      required
                    />
                  </FormField>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField label="Type de prestation *" id="service_type">
                    <Select
                      value={form.service_type}
                      onValueChange={(v) => update("service_type", v)}
                    >
                      <SelectTrigger
                        id="service_type"
                        className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E]"
                        data-testid="select-service-type"
                      >
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Type de local *" id="location_type">
                    <Select
                      value={form.location_type}
                      onValueChange={(v) => update("location_type", v)}
                    >
                      <SelectTrigger
                        id="location_type"
                        className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E]"
                        data-testid="select-location-type"
                      >
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCATION_TYPES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <FormField label="Surface (m²)" id="surface">
                    <Input
                      id="surface"
                      value={form.surface}
                      onChange={(e) => update("surface", e.target.value)}
                      placeholder="ex: 200"
                      className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E]"
                      data-testid="input-surface"
                    />
                  </FormField>
                  <FormField label="Fréquence" id="frequency">
                    <Select
                      value={form.frequency}
                      onValueChange={(v) => update("frequency", v)}
                    >
                      <SelectTrigger
                        id="frequency"
                        className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E]"
                        data-testid="select-frequency"
                      >
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Ville" id="city">
                    <Input
                      id="city"
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      placeholder="Paris, Neuilly..."
                      className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E]"
                      data-testid="input-city"
                    />
                  </FormField>
                </div>

                <FormField label="Votre message" id="message">
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Décrivez brièvement votre besoin, contraintes horaires, etc."
                    rows={4}
                    className="bg-white border-slate-200 rounded-none focus:ring-[#2E9E6E] focus:border-[#2E9E6E] resize-none"
                    data-testid="input-message"
                  />
                </FormField>

                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center justify-center gap-3 bg-[#0F2B5B] hover:bg-[#153a7a] disabled:opacity-60 text-white px-8 py-4 text-sm font-medium tracking-wide transition-colors w-full md:w-auto"
                  data-testid="quote-submit-btn"
                >
                  {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.75} />
                </button>
                <p className="text-xs text-slate-500">
                  Vos données sont utilisées uniquement pour traiter votre
                  demande. Aucun partage avec des tiers.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, id, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.15em] text-slate-600 font-medium">
        {label}
      </Label>
      {children}
    </div>
  );
}
